'use client'

import { useTranslations } from 'next-intl'
import { Toast } from '@skop-app/lib-front'

import { useUserContext } from '@/context'
import { useCreateProduct } from '@/services/mutations'
import { useUpsertClient } from '@/services/mutations/customers'
import { useUpsertProduct } from '@/services/mutations/product'

import { useGuardedMutation } from './guardedMutation'
import {
  canManageOrderCustomers,
  canManageOrderServices,
  resolveCustomerOrderMutationOperation,
  resolveServiceOrderMutationOperation,
} from './order.permissions'

export const useAuthorizedUpsertClient = () => {
  const mutation = useUpsertClient()
  const { authorization } = useUserContext()
  const t = useTranslations()
  const customerPermissionMessage = t('Customers-directory.permission-denied')

  const mutate = (...args: Parameters<typeof mutation.mutate>) => {
    const [variables, options] = args
    const operation = resolveCustomerOrderMutationOperation(
      variables.upsertType,
    )
    if (!canManageOrderCustomers(authorization, operation)) {
      Toast.error(customerPermissionMessage)
      return
    }

    mutation.mutate(variables, options)
  }

  const mutateAsync = async (
    ...args: Parameters<NonNullable<typeof mutation.mutateAsync>>
  ) => {
    const [variables, options] = args
    const operation = resolveCustomerOrderMutationOperation(
      variables.upsertType,
    )
    if (!canManageOrderCustomers(authorization, operation)) {
      Toast.error(customerPermissionMessage)
      throw new Error(customerPermissionMessage)
    }

    return mutation.mutateAsync?.(variables, options)
  }

  return {
    ...mutation,
    mutate,
    mutateAsync,
  }
}

export const useAuthorizedCreateProduct = () => {
  const mutation = useCreateProduct()
  const { authorization } = useUserContext()
  const t = useTranslations()

  return useGuardedMutation(
    mutation,
    canManageOrderServices(authorization, 'create'),
    t('Services-directory.permission-denied'),
  )
}

export const useAuthorizedUpsertProduct = () => {
  const mutation = useUpsertProduct()
  const { authorization } = useUserContext()
  const t = useTranslations()
  const servicePermissionMessage = t('Services-directory.permission-denied')

  const mutate = (...args: Parameters<typeof mutation.mutate>) => {
    const [variables, options] = args
    const operation = resolveServiceOrderMutationOperation(variables)
    if (!canManageOrderServices(authorization, operation)) {
      Toast.error(servicePermissionMessage)
      return
    }

    mutation.mutate(variables, options)
  }

  const mutateAsync = async (
    ...args: Parameters<NonNullable<typeof mutation.mutateAsync>>
  ) => {
    const [variables, options] = args
    const operation = resolveServiceOrderMutationOperation(variables)
    if (!canManageOrderServices(authorization, operation)) {
      Toast.error(servicePermissionMessage)
      throw new Error(servicePermissionMessage)
    }

    return mutation.mutateAsync?.(variables, options)
  }

  return {
    ...mutation,
    mutate,
    mutateAsync,
  }
}
